/**
 * Ambassador referral attribution helper.
 *
 * - Persists first-touch ref code in localStorage + cookie for 90 days.
 * - Two entry points:
 *     1. <RefCapture /> route — handles /ref/:code → bounce to /.
 *     2. captureRefFromQuery() — called on every page mount inside AppShell to
 *        catch ?ref=CODE on any landing URL.
 *
 * The actual server-side attribution (link referrer ↔ user) happens at:
 *   - POST /api/auth/register  (we send the code in the body)
 *   - POST /api/memberships/checkout  (we send the code in the body)
 *   - server's existing /api/referrals/track endpoint logs the click.
 */
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";

const REF_KEY = "ff2d_ref";
const REF_TTL_DAYS = 90;
const REF_COOKIE = "ff2d_ref";

function setCookie(name, value, days) {
  try {
    const d = new Date();
    d.setTime(d.getTime() + days * 86400 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
  } catch {
    /* private mode */
  }
}

export function setRefCode(code) {
  if (!code || typeof code !== "string") return;
  const clean = code.trim().toUpperCase().slice(0, 32);
  if (!clean) return;
  // First-touch wins — if we already have one, don't overwrite.
  if (localStorage.getItem(REF_KEY)) return;
  try {
    localStorage.setItem(REF_KEY, clean);
    localStorage.setItem(REF_KEY + "_at", new Date().toISOString());
  } catch {
    /* no-op */
  }
  setCookie(REF_COOKIE, clean, REF_TTL_DAYS);
  // Best-effort click attribution — fire-and-forget.
  api.post("/referrals/click", { code: clean }).catch(() => {});
}

export function getRefCode() {
  try {
    const v = localStorage.getItem(REF_KEY);
    const at = localStorage.getItem(REF_KEY + "_at");
    if (!v || !at) return null;
    // TTL check — drop the code after REF_TTL_DAYS.
    const ageMs = Date.now() - new Date(at).getTime();
    if (ageMs > REF_TTL_DAYS * 86400 * 1000) {
      localStorage.removeItem(REF_KEY);
      localStorage.removeItem(REF_KEY + "_at");
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

export function clearRefCode() {
  try {
    localStorage.removeItem(REF_KEY);
    localStorage.removeItem(REF_KEY + "_at");
  } catch {
    /* no-op */
  }
  setCookie(REF_COOKIE, "", -1);
}

export function captureRefFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref");
    if (code) setRefCode(code);
  } catch {
    /* no-op */
  }
}

export default function RefCapture() {
  const { code } = useParams();
  const nav = useNavigate();
  useEffect(() => {
    if (code) setRefCode(code);
    nav("/", { replace: true });
  }, [code, nav]);
  return <div className="p-10 text-center text-[var(--ff-text-muted)]">Tracking referral…</div>;
}
